'use client';

import { useEffect, useMemo, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Stack } from '@mui/material';
import { TickerPriceHistoryChart } from './TickerPriceHistoryChart';
import { buildBenchmarkRequest, type BenchmarkSource } from '@/libs/price-history-benchmarks';

type PricePoint = {
  date: string;
  close: number;
};

type ApiResponse = {
  ticker: string;
  series: PricePoint[];
};

type MarketSeriesResponse = {
  symbols: string[];
  series: Record<string, Array<{ date: string; value: number }>>;
};

type BenchmarkOption = {
  key: string;
  label: string;
  source: BenchmarkSource;
};

type TickerPriceHistoryPanelProps = {
  tickerLabel: string;
  corporateEvents: Array<{
    date: string;
    eventType: string;
    label: string | null;
    splitMultiplier: number | null;
    subscriptionPercentage: number | null;
  }>;
};

const DEFAULT_BENCHMARKS: BenchmarkOption[] = [
  { key: 'IFIX', label: 'IFIX', source: 'market_series' },
  { key: 'SELIC', label: 'SELIC', source: 'market_series' },
  { key: 'CDI', label: 'CDI', source: 'market_series' },
];

const DEFAULT_SELECTED_KEYS = ['IFIX'];
const getDayKeyInBrt = () =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());

const CACHE_ROLLOVER_HOUR_BRT = 18;
const BUSINESS_DAYS_PER_YEAR = 252;

const getPreviousDay = (dayKey: string) => {
  const base = new Date(`${dayKey}T00:00:00Z`);
  const previous = new Date(base.getTime() - 24 * 60 * 60 * 1000);
  return previous.toISOString().slice(0, 10);
};

const toAnnualPercentIfDaily = (value: number) => {
  if (!Number.isFinite(value)) return value;
  if (value > 1) return value;
  const daily = value / 100;
  const annual = Math.pow(1 + daily, BUSINESS_DAYS_PER_YEAR) - 1;
  return annual * 100;
};

const normalizeBenchmarkSeries = (key: string, series: Array<{ date: string; value: number }>) => {
  if (key !== 'SELIC' && key !== 'CDI') return series;
  return series.map((item) => ({
    ...item,
    value: toAnnualPercentIfDaily(item.value),
  }));
};

const getCacheDayKeyInBrt = () => {
  const now = new Date();
  const dayKey = getDayKeyInBrt();
  const hourInBrt = Number(
    new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Sao_Paulo',
      hour: '2-digit',
      hour12: false,
    }).format(now),
  );
  if (Number.isNaN(hourInBrt)) {
    return dayKey;
  }
  return hourInBrt >= CACHE_ROLLOVER_HOUR_BRT ? dayKey : getPreviousDay(dayKey);
};

export function TickerPriceHistoryPanel({ tickerLabel, corporateEvents }: TickerPriceHistoryPanelProps) {
  const [series, setSeries] = useState<PricePoint[] | null>(null);
  const [benchmarkSeriesByKey, setBenchmarkSeriesByKey] = useState<
    Record<string, Array<{ date: string; value: number }>>
  >({});
  const [benchmarkOptions, setBenchmarkOptions] = useState<BenchmarkOption[]>(DEFAULT_BENCHMARKS);
  const [selectedBenchmarkKeys, setSelectedBenchmarkKeys] = useState<string[]>(DEFAULT_SELECTED_KEYS);
  const [isBenchmarksModalOpen, setIsBenchmarksModalOpen] = useState(false);
  const [draftBenchmarkKeys, setDraftBenchmarkKeys] = useState<string[]>(DEFAULT_SELECTED_KEYS);
  const [error, setError] = useState<string | null>(null);
  const dayKey = useMemo(() => getCacheDayKeyInBrt(), []);

  const optionByKey = useMemo(() => new Map(benchmarkOptions.map((item) => [item.key, item])), [benchmarkOptions]);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();

    const loadIndexOptions = async () => {
      try {
        const response = await fetch('/api/catalogo/tickers?fundType=INDEX&includeIndexes=1', {
          method: 'GET',
          cache: 'default',
          signal: controller.signal,
        });
        if (!response.ok || !active) return;
        const data = (await response.json()) as Array<{ idCem: string; name?: string | null }>;
        const fetchedOptions: BenchmarkOption[] = data
          .map((item) => {
            const key = item.idCem;
            return {
              key,
              label: key,
              source: 'market_series',
            } as BenchmarkOption;
          })
          .sort((a, b) => a.label.localeCompare(b.label));
        const byKey = new Map<string, BenchmarkOption>(fetchedOptions.map((item) => [item.key, item]));
        for (const fallback of DEFAULT_BENCHMARKS) {
          if (!byKey.has(fallback.key)) {
            byKey.set(fallback.key, fallback);
          }
        }
        const indexOptions = Array.from(byKey.values()).sort((a, b) => a.label.localeCompare(b.label));
        if (!active) return;
        setBenchmarkOptions(indexOptions.length > 0 ? indexOptions : DEFAULT_BENCHMARKS);
      } catch {
        if (!active) return;
      }
    };

    void loadIndexOptions();

    return () => {
      active = false;
      controller.abort();
    };
  }, []);

  useEffect(() => {
    const available = new Set(benchmarkOptions.map((item) => item.key));
    const normalizedSelection = selectedBenchmarkKeys.filter((key) => available.has(key));
    if (normalizedSelection.length === selectedBenchmarkKeys.length) return;
    if (normalizedSelection.length > 0) {
      setSelectedBenchmarkKeys(normalizedSelection);
      setDraftBenchmarkKeys(normalizedSelection);
      return;
    }
    const fallback = available.has('IFIX') ? 'IFIX' : benchmarkOptions.length > 0 ? benchmarkOptions[0].key : null;
    const next = fallback ? [fallback] : [];
    setSelectedBenchmarkKeys(next);
    setDraftBenchmarkKeys(next);
  }, [benchmarkOptions, selectedBenchmarkKeys]);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();

    const load = async () => {
      try {
        setError(null);
        const response = await fetch(`/api/ticker-price-history/${encodeURIComponent(tickerLabel)}?day=${dayKey}`, {
          method: 'GET',
          cache: 'default',
          signal: controller.signal,
        });
        if (!response.ok) {
          throw new Error(`Erro ao carregar histórico (${response.status})`);
        }
        const data = (await response.json()) as ApiResponse;
        if (!active) return;
        setSeries(data.series);
      } catch (err) {
        if (!active) return;
        const message = err instanceof Error ? err.message : 'Erro ao carregar histórico';
        setError(message);
      }
    };

    void load();

    return () => {
      active = false;
      controller.abort();
    };
  }, [dayKey, tickerLabel]);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();

    const loadBenchmarks = async () => {
      if (!series || series.length === 0 || selectedBenchmarkKeys.length === 0) {
        if (active) setBenchmarkSeriesByKey({});
        return;
      }

      const firstDate = series[0].date.slice(0, 10);
      const lastDate = series[series.length - 1].date.slice(0, 10);

      const pairs = await Promise.all(
        selectedBenchmarkKeys.map(async (key) => {
          const option = optionByKey.get(key);
          if (!option) return [key, [] as Array<{ date: string; value: number }>] as const;
          const request = buildBenchmarkRequest({
            key,
            source: option.source,
            fromDate: firstDate,
            toDate: lastDate,
            dayKey,
          });

          try {
            if (request.kind === 'market_series') {
              const response = await fetch(request.url, {
                method: 'GET',
                cache: 'default',
                signal: controller.signal,
              });
              if (!response.ok) return [key, [] as Array<{ date: string; value: number }>] as const;
              const payload = (await response.json()) as MarketSeriesResponse;
              return [key, normalizeBenchmarkSeries(key, payload.series[key] ?? [])] as const;
            }

            const response = await fetch(request.url, {
              method: 'GET',
              cache: 'default',
              signal: controller.signal,
            });
            if (!response.ok) return [key, [] as Array<{ date: string; value: number }>] as const;
            const payload = (await response.json()) as ApiResponse;
            return [
              key,
              normalizeBenchmarkSeries(
                key,
                payload.series.map((item) => ({
                  date: item.date.slice(0, 10),
                  value: item.close,
                })),
              ),
            ] as const;
          } catch {
            return [key, [] as Array<{ date: string; value: number }>] as const;
          }
        }),
      );

      if (!active) return;
      setBenchmarkSeriesByKey(Object.fromEntries(pairs));
    };

    void loadBenchmarks();

    return () => {
      active = false;
      controller.abort();
    };
  }, [dayKey, optionByKey, selectedBenchmarkKeys, series]);

  const openBenchmarksModal = () => {
    setDraftBenchmarkKeys(selectedBenchmarkKeys);
    setIsBenchmarksModalOpen(true);
  };

  const closeBenchmarksModal = () => {
    setSelectedBenchmarkKeys([...draftBenchmarkKeys]);
    setIsBenchmarksModalOpen(false);
  };

  const toggleDraftBenchmark = (key: string) => {
    setDraftBenchmarkKeys((prev) => (prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]));
  };

  if (error) {
    return (
      <Paper variant='outlined' sx={{ mt: 2, p: 2, borderStyle: 'dashed', color: 'error.main' }}>
        {error}
      </Paper>
    );
  }

  if (series === null) {
    return (
      <Paper variant='outlined' sx={{ mt: 2, p: 2, borderStyle: 'dashed', color: 'text.secondary' }}>
        Carregando histórico de preços...
      </Paper>
    );
  }

  if (series.length === 0) {
    return (
      <Paper variant='outlined' sx={{ mt: 2, p: 2, borderStyle: 'dashed', color: 'text.secondary' }}>
        Ainda não há histórico de preços para este fundo.
      </Paper>
    );
  }

  const benchmarks = selectedBenchmarkKeys
    .map((key) => {
      const option = optionByKey.get(key);
      if (!option) return null;
      return {
        key,
        label: option.label,
        series: benchmarkSeriesByKey[key] ?? [],
      };
    })
    .filter(
      (item): item is { key: string; label: string; series: Array<{ date: string; value: number }> } => item !== null,
    );

  return (
    <Stack spacing={1.5} sx={{ mt: 2 }}>
      <Dialog open={isBenchmarksModalOpen} onClose={closeBenchmarksModal} maxWidth='xs' fullWidth>
        <DialogTitle>Selecionar benchmarks</DialogTitle>
        <DialogContent dividers sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {benchmarkOptions.map((option) => {
              const selected = draftBenchmarkKeys.includes(option.key);
              return (
                <Button
                  key={option.key}
                  size='small'
                  variant={selected ? 'contained' : 'outlined'}
                  onClick={() => toggleDraftBenchmark(option.key)}
                >
                  {option.label}
                </Button>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant='contained' onClick={closeBenchmarksModal}>
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <TickerPriceHistoryChart
        tickerLabel={tickerLabel}
        priceSeries={series}
        corporateEvents={corporateEvents}
        benchmarks={benchmarks}
        onOpenBenchmarks={openBenchmarksModal}
      />
    </Stack>
  );
}
